// One transport, one client per service. Cookie-authenticated: the browser
// sends the console session cookie on every call; Connect adds the protocol
// header that makes the request CSRF-safe.

import { Code, ConnectError, createClient, type Interceptor } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { AndroidAutoService } from '@motionsafe/ms-console-api/admin/v1/android_auto_pb.js';
import { AuditService } from '@motionsafe/ms-console-api/admin/v1/audit_pb.js';
import { ContentService } from '@motionsafe/ms-console-api/admin/v1/content_pb.js';
import { DeviceService } from '@motionsafe/ms-console-api/admin/v1/device_pb.js';
import { KeyService } from '@motionsafe/ms-console-api/admin/v1/key_pb.js';
import { LegalService } from '@motionsafe/ms-console-api/admin/v1/legal_pb.js';
import { GroupMappingService } from '@motionsafe/ms-console-api/admin/v1/mapping_pb.js';
import { NotificationService } from '@motionsafe/ms-console-api/admin/v1/notification_pb.js';
import { OrgService } from '@motionsafe/ms-console-api/admin/v1/org_pb.js';
import { UserService } from '@motionsafe/ms-console-api/admin/v1/user_pb.js';
import { VehicleDataService } from '@motionsafe/ms-console-api/admin/v1/vehicle_data_pb.js';

// A session that ends while a page is open (it expired or was revoked) makes
// the next call answer Unauthenticated, and the page goes back to sign-in,
// which brings the person back here afterwards. A full load rather than a
// route change, so nothing read under the old session stays in memory, the
// same way sign-in itself lands. The account modals call /auth/* with fetch,
// so a wrong password there never comes through here.
let leaving = false;

/** Called as the person signs out, so a call their own sign-out refuses does not send them to sign-in as if the session had ended. */
export function signingOut(): void {
  leaving = true;
}

const sessionEnded: Interceptor = (next) => async (req) => {
  try {
    return await next(req);
  } catch (e) {
    if (!(e instanceof ConnectError && e.code === Code.Unauthenticated)) throw e;
    if (!leaving) {
      leaving = true;
      const { pathname, search } = window.location;
      window.location.replace(`/?${new URLSearchParams({ next: `${pathname}${search}`, ended: '1' }).toString()}`);
    }
    // The page is on its way out: the call never answers, so no error wall
    // or toast says something failed while sign-in loads.
    return new Promise<never>(() => {});
  }
};

const transport = createConnectTransport({ baseUrl: '/api', interceptors: [sessionEnded] });

export const api = {
  org: createClient(OrgService, transport),
  user: createClient(UserService, transport),
  key: createClient(KeyService, transport),
  device: createClient(DeviceService, transport),
  mapping: createClient(GroupMappingService, transport),
  audit: createClient(AuditService, transport),
  notification: createClient(NotificationService, transport),
  vehicleData: createClient(VehicleDataService, transport),
  androidAuto: createClient(AndroidAutoService, transport),
  content: createClient(ContentService, transport),
  legal: createClient(LegalService, transport),
};

export interface Me {
  signedIn: true;
  userId: string;
  orgId: string;
  consoleRole: 'ms_superadmin' | 'ms_staff' | 'org_admin';
  /** For `ms_staff`, the staff roles they hold, as row words ("fulfillment"). Empty is read only. */
  staffRoles?: string[];
  displayName: string;
  /** The account menu offers Change authenticator: a password session whose account has 2FA. */
  authenticator?: boolean;
  /** The account menu offers Change email: a session not signed in through the organization's own identity provider. */
  changeEmail?: boolean;
}

/** Thrown when the service says it has no secrets yet; the sign-in page shows the sentence. */
export class NotConfiguredError extends Error {
  /** Set when the service failed at a setup stage (keys, database, identity) rather than lacking a secret. */
  readonly stage: string | undefined;
  /** The failure as the service saw it, without the sentence around it. */
  readonly reason: string | undefined;
  constructor(readonly missing: string[], message: string, stage?: string, reason?: string) {
    super(message);
    this.name = 'NotConfiguredError';
    this.stage = stage;
    this.reason = reason;
  }
}

export async function fetchMe(): Promise<Me | null> {
  const res = await fetch('/api/me', { credentials: 'same-origin' });
  if (res.status === 401) return null;
  if (res.status === 503) {
    const body = (await res.json().catch(() => ({}))) as { configured?: boolean; missing?: string[]; message?: string; stage?: string; reason?: string };
    if (body.configured === false) throw new NotConfiguredError(body.missing ?? [], body.message ?? 'This console is not configured yet.', body.stage, body.reason);
  }
  if (!res.ok) throw new Error(`me: ${res.status}`);
  return (await res.json()) as Me;
}
